import type { WizardFlowDefinition } from '../flowTypes';
import type { SelfServiceData } from '../../types';
import { requireContactInfo, requireTimeSelection } from '../sharedValidators';
import StartStep from '../../components/steps/StartStep';
import LoginStep from '../../components/steps/LoginStep';
import AskForWhomStep from '../../components/steps/AskForWhomStep';
import ChildPassportInfoStep from '../../components/steps/ChildPassportInfoStep';
import AskHasOldPassportStep from '../../components/steps/AskHasOldPassportStep';
import AskPassportDamageStep from '../../components/steps/AskPassportDamageStep';
import DeclarationLostPassportStep from '../../components/steps/DeclarationLostPassportStep';
import AskPreviousCitizenshipStep from '../../components/steps/AskPreviousCitizenshipStep';
import WarnDocumentationStep from '../../components/steps/WarnDocumentationStep';
import PhotoMethodStep from '../../components/steps/PhotoMethodStep';
import PhotoInfoStep from '../../components/steps/PhotoInfoStep';
import ContactInfoStep from '../../components/steps/ContactInfoStep';
import TimeSelectionStep from '../../components/steps/TimeSelectionStep';
import SummaryStep from '../../components/steps/SummaryStep';
import PaymentStep from '../../components/steps/PaymentStep';
import ConfirmationStep from '../../components/steps/ConfirmationStep';
import SignStep from '../../components/sign/SignStep';
import CustodyStep from '../../components/passport/CustodyStep';
import MedindehaverDetailsStep from '../../components/passport/MedindehaverDetailsStep';
import AddressingNameStep from '../../components/passport/AddressingNameStep';
import PassportHeightStep from '../../components/passport/PassportHeightStep';
import GdprStep from '../../components/shared/GdprStep';

const requirePassportOwner = (data: SelfServiceData): string | null => {
  return data.passportFor ? null : 'Vælg venligst, hvem passet er til.';
};

const requirePassportDamageAnswer = (data: SelfServiceData): string | null => {
  return data.passportDamage ? null : 'Vælg venligst, om passet er beskadiget.';
};

const requirePreviousCitizenshipAnswer = (data: SelfServiceData): string | null => {
  return data.previousCitizenship !== undefined ? null : 'Vælg venligst Ja eller Nej.';
};

const requireOldPassportAnswer = (data: SelfServiceData): string | null => {
  return data.hasOldPassport !== undefined ? null : 'Vælg venligst et svar.';
};
const requirePassportHeight = (data: SelfServiceData): string | null => {
  if (typeof data.passportHeight !== 'number') {
    return 'Angiv højden i centimeter.';
  }
  if (data.passportHeight < 30 || data.passportHeight > 300) {
    return 'Højden skal være mellem 30 og 300 cm.';
  }
  return null;
};


const passportPhases = [
  { id: 'intro', name: 'Start' },
  { id: 'information', name: 'Oplysninger' },
  { id: 'booking', name: 'Tid & kontakt' },
  { id: 'approval', name: 'Godkend' },
  { id: 'completion', name: 'Afslut' }
] as const;

const passportFlow: WizardFlowDefinition = {
  id: 'pas',
  startStep: 'start',
  phases: passportPhases,
  steps: {
    start: { id: 'start', name: 'Introduktion', component: StartStep, next: 'gdpr', phase: 'intro' },
    gdpr: { id: 'gdpr', name: 'GDPR', component: GdprStep, next: 'login', phase: 'intro' },
    login: { id: 'login', name: 'Log ind', component: LoginStep, next: 'ask_for_whom', phase: 'intro' },
    ask_for_whom: {
      id: 'ask_for_whom',
      name: 'Hvem er passet til?',
      component: AskForWhomStep,
      validate: requirePassportOwner,
      getNext: (data) => (data.passportFor === 'child' ? 'child_info' : 'ask_has_old_passport'),
      phase: 'information'
    },
    child_info: { id: 'child_info', name: 'Pas til barn', component: ChildPassportInfoStep, next: 'custody', phase: 'information' },
    custody: {
      id: 'custody',
      name: 'Forældremyndighed',
      component: CustodyStep,
      getNext: (data) => (data.custodyBothPresent ? 'ask_has_old_passport' : 'medindehaver_details'),
      phase: 'information'
    },
    medindehaver_details: {
      id: 'medindehaver_details',
      name: 'Medindehaver',
      component: MedindehaverDetailsStep,
      next: 'ask_has_old_passport',
      phase: 'information'
    },
    ask_has_old_passport: {
      id: 'ask_has_old_passport',
      name: 'Har du et gyldigt pas?',
      component: AskHasOldPassportStep,
      validate: requireOldPassportAnswer,
      getNext: (data) => {
        if (data.hasOldPassport === false) {return 'ask_previous_citizenship';}
        if (data.hasOldPassport === true) {return 'ask_passport_damage';}
        return 'ask_has_old_passport';
      },
      phase: 'information'
    },
    ask_passport_damage: {
      id: 'ask_passport_damage',
      name: 'Er passet beskadiget?',
      component: AskPassportDamageStep,
      validate: requirePassportDamageAnswer,
      getNext: (data) => (data.passportDamage === 'lost' ? 'declaration_lost' : 'ask_height'),
      phase: 'information'
    },
    declaration_lost: {
      id: 'declaration_lost',
      name: 'Erklæring',
      component: DeclarationLostPassportStep,
      next: 'ask_height',
      phase: 'information'
    },
    ask_previous_citizenship: {
      id: 'ask_previous_citizenship',
      name: 'Tidligere statsborgerskab',
      component: AskPreviousCitizenshipStep,
      validate: requirePreviousCitizenshipAnswer,
      getNext: (data) => (data.previousCitizenship ? 'warn_documentation' : 'ask_height'),
      phase: 'information'
    },
    warn_documentation: {
      id: 'warn_documentation',
      name: 'Dokumentation',
      component: WarnDocumentationStep,
      next: 'ask_height',
      phase: 'information'
    },
    ask_height: {
      id: 'ask_height',
      name: 'Højde',
      component: PassportHeightStep,
      validate: requirePassportHeight,
      next: 'photo_method',
      phase: 'information'
    },
    photo_method: {
      id: 'photo_method',
      name: 'Foto',
      component: PhotoMethodStep,
      validate: (data) => (data.photoMethod ? null : 'Vælg venligst, hvor du får taget foto.'),
      next: 'photo_info',
      phase: 'booking'
    },
    photo_info: { id: 'photo_info', name: 'Pasfoto', component: PhotoInfoStep, next: 'contact_info', phase: 'booking' },
    contact_info: {
      id: 'contact_info',
      name: 'Kontakt',
      component: ContactInfoStep,
      validate: requireContactInfo,
      getNext: (data) => (data.user?.hasAddressingName ? 'addressing_name' : 'select_time'),
      phase: 'booking'
    },
    addressing_name: {
      id: 'addressing_name',
      name: 'Adresseringsnavn',
      component: AddressingNameStep,
      next: 'select_time',
      phase: 'booking'
    },
    select_time: {
      id: 'select_time',
      name: 'Vælg tid',
      component: TimeSelectionStep,
      validate: requireTimeSelection,
      next: 'summary',
      phase: 'booking'
    },
    summary: { id: 'summary', name: 'Oversigt', component: SummaryStep, next: 'sign', phase: 'approval' },
    sign: { id: 'sign', name: 'Signér', component: SignStep, next: 'payment', phase: 'approval' },
    payment: { id: 'payment', name: 'Betaling', component: PaymentStep, next: 'confirmation', phase: 'approval' },
    confirmation: { id: 'confirmation', name: 'Bekræftelse', component: ConfirmationStep, phase: 'completion' }
  }
};

export { passportFlow };









